import type { FC } from "react"
import { useCallback, useState } from "react"

import { GoodsReceiptFormDialog } from "../../subfeatures/Incoming/components/GoodsReceiptFormDialog"
import {
  useCreateGoodsReceipt,
  usePurchaseOrderSelectionDialog,
  useUpdateGoodsReceipt,
} from "../../subfeatures/Incoming/hooks"
import { type GoodsReceiptFormValues } from "../../subfeatures/Incoming/schemas"
import { serializeGoodsReceiptForm } from "../../subfeatures/Incoming/utils"

import { PurchaseOrderSelectionDialog } from "./PurchaseOrderSelectionDialog"

import CancelDialog from "@/components/common/Modals/CancelDialog"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { GoodReceiptDialogType } from "@/screens/WarehouseReceiving/types"

interface PurchaseOrderSelectionWithFormContainerProps {
  onClose: () => void
  onSubmit: () => void
  dialogType?: GoodReceiptDialogType
  goodsReceipt?: GoodsReceipt
}

export const PurchaseOrderSelectionWithFormContainer: FC<PurchaseOrderSelectionWithFormContainerProps> = ({
  onClose,
  onSubmit,
  dialogType,
  goodsReceipt,
}) => {
  const [currentDialog, setCurrentDialog] = useState<GoodReceiptDialogType>(
    dialogType || GoodReceiptDialogType.PurchaseOrderSelection
  )
  const [hasPurchaseOrder, setHasPurchaseOrder] = useState(!!goodsReceipt?.orders?.length)
  const [isSubmittingForm, setIsSubmittingForm] = useState(false)

  const {
    searchQuery,
    inputValue,
    selectedPurchaseOrders,
    purchaseOrders,
    isFetching,
    handleSearch,
    handleInputChange,
    handleSelect,
    reset,
  } = usePurchaseOrderSelectionDialog()

  const handleCloseWithReset = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const { create: createGoodsReceipt } = useCreateGoodsReceipt(handleCloseWithReset)
  const { update: updateGoodsReceipt } = useUpdateGoodsReceipt({ onSuccess: handleCloseWithReset })

  const hasSelections = selectedPurchaseOrders.length > 0
  const hasSearchQuery = Boolean(searchQuery)

  const handlePurchaseOrderDialogClose = useCallback(() => {
    if (hasSelections || hasSearchQuery) {
      setCurrentDialog(GoodReceiptDialogType.CancelConfirmation)
    } else {
      handleCloseWithReset()
    }
  }, [hasSelections, hasSearchQuery, handleCloseWithReset])

  const handleContinue = (withPurchaseOrder: boolean) => {
    setHasPurchaseOrder(withPurchaseOrder)
    setCurrentDialog(GoodReceiptDialogType.GoodsReceipt)
  }

  const getOrderIds = useCallback((): number[] => {
    if (goodsReceipt) {
      return goodsReceipt.orders?.map((o) => o.orderId) || []
    }
    return hasPurchaseOrder ? selectedPurchaseOrders.map((purchaseOrder) => purchaseOrder.orderId) : []
  }, [goodsReceipt, hasPurchaseOrder, selectedPurchaseOrders])

  const submitForm = useCallback(
    async (formData: GoodsReceiptFormValues) => {
      setIsSubmittingForm(true)
      try {
        const orderIds = getOrderIds()
        const payload = serializeGoodsReceiptForm(formData, orderIds)

        if (goodsReceipt) {
          await updateGoodsReceipt({
            goodsReceiptId: goodsReceipt.goodsReceiptId,
            ...payload,
          })
        } else {
          await createGoodsReceipt({
            status: GoodsReceiptStatus.Incoming,
            ...payload,
          })
        }
        onSubmit()
      } catch (error) {
        console.error("Error submitting form:", error)
        // Error handling is done by the hook (shows notification)
      } finally {
        setIsSubmittingForm(false)
      }
    },
    [getOrderIds, goodsReceipt, onSubmit, updateGoodsReceipt, createGoodsReceipt]
  )

  const handleFormClose = () => {
    if (goodsReceipt) {
      handleCloseWithReset()
    } else {
      setCurrentDialog(GoodReceiptDialogType.PurchaseOrderSelection)
    }
  }

  const handleCancelDialogClose = () => {
    setCurrentDialog(GoodReceiptDialogType.PurchaseOrderSelection)
  }

  switch (currentDialog) {
    case GoodReceiptDialogType.PurchaseOrderSelection:
      return (
        <PurchaseOrderSelectionDialog
          searchQuery={searchQuery}
          inputValue={inputValue}
          purchaseOrders={purchaseOrders}
          isFetching={isFetching}
          selectedPurchaseOrders={selectedPurchaseOrders}
          goodsReceipt={goodsReceipt}
          onSearch={handleSearch}
          onInputChange={handleInputChange}
          onSelect={handleSelect}
          onClose={handlePurchaseOrderDialogClose}
          onContinue={handleContinue}
        />
      )

    case GoodReceiptDialogType.GoodsReceipt:
      return (
        <GoodsReceiptFormDialog
          onClose={handleFormClose}
          onSubmit={submitForm}
          hasPurchaseOrder={hasPurchaseOrder}
          isLoading={isSubmittingForm}
          goodsReceipt={goodsReceipt}
          selectedPurchaseOrders={selectedPurchaseOrders}
        />
      )

    case GoodReceiptDialogType.CancelConfirmation:
      return <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleCloseWithReset} />

    default:
      return null
  }
}
